import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Stack,
  Button,
  CircularProgress,
  Skeleton,
  Alert,
  Paper,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import moment from 'moment';
import { axiosGetCall } from 'src/services/apisCall';
import { renderErrorMessage, renderSuccessMessage } from 'src/lib/toastMessage';
import AdminSettingDetailsModal from './AdminSettingDetailsModal';

const ACTION_TYPE_OPTIONS = [
  { value: 'all', label: 'All Actions' },
  { value: 'A_required', label: 'Answer Count (A)' },
  { value: 'NA_required', label: 'Not Answer Count (NA)' },
  { value: 'BZ_required', label: 'Busy Count (BZ)' },
  { value: 'O_required', label: 'Other Count (O)' },
  { value: 'IVR_required', label: 'IVR Update' },
  { value: 'DTMF_required', label: 'DTMF Update' },
  { value: 'CallDuration_required', label: 'Call Duration' },
  { value: 'DateTime_required', label: 'Campaign Date & Time' },
];

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Status' },
  { value: '0', label: 'Pending' },
  { value: '1', label: 'Processed'},
];

export default function AdminSettingTable() {
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Filters state
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Selected item for details modal
  const [selectedSetting, setSelectedSetting] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const skip = page * rowsPerPage;
      const params = {
        skip,
        limit: rowsPerPage,
        page: page + 1,
      };

      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      if (search.trim()) {
        params.campaignId = search.trim();
      }

      const response = await axiosGetCall('getAdminSettings', params);
      const list = response?.list || response?.data || [];
      const total = response?.totalCount ?? list.length;

      setData(list);
      setTotalCount(total);
    } catch (err) {
      console.error('Error fetching admin settings:', err);
      const msg = err?.message || 'Failed to fetch admin settings list';
      setError(msg);
      renderErrorMessage(msg);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, statusFilter]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Client-side filtering fallback for action types & search query
  const filteredData = useMemo(() => {
    return data.filter((row) => {
      // Action Type filter
      if (actionFilter !== 'all') {
        if (!row[actionFilter]) return false;
      }

      // Search filter across campaignIds, selectedUserId, email, requested_by, seq_id
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        const inSeq = String(row.seq_id || '').toLowerCase().includes(q);
        const inCampaigns = (row.campaignIds || []).some((id) => String(id).toLowerCase().includes(q));
        const inUser = String(row.selectedUserId || '').toLowerCase().includes(q);
        const inEmail = String(row.email || '').toLowerCase().includes(q);
        const inReq = String(row.requested_by || '').toLowerCase().includes(q);
        if (!inSeq && !inCampaigns && !inUser && !inEmail && !inReq) return false;
      }

      // Status filter
      if (statusFilter !== 'all') {
        if (String(row.status) !== String(statusFilter)) return false;
      }

      return true;
    });
  }, [data, actionFilter, search, statusFilter]);

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleResetFilters = () => {
    setSearch('');
    setActionFilter('all');
    setStatusFilter('all');
    setPage(0);
  };

  const handleCopy = (text, e) => {
    e?.stopPropagation();
    navigator.clipboard.writeText(text);
    renderSuccessMessage(`Copied "${text}" to clipboard!`);
  };

  const handleOpenDetails = (row) => {
    setSelectedSetting(row);
    setDetailsOpen(true);
  };

  // Helper to extract action chips
  const renderActionChips = (row) => {
    const chips = [];
    if (row.A_required) {
      chips.push(
        <Chip
          key="A"
          label={`Answer (${row.A_required.count}${row.A_required.countMode === 'percent' ? '%' : ''})`}
          size="small"
          color="primary"
          variant="soft"
          sx={{ fontWeight: 600 }}
        />
      );
    }
    if (row.NA_required) {
      chips.push(
        <Chip
          key="NA"
          label={`Not Answer (${row.NA_required.count}${row.NA_required.countMode === 'percent' ? '%' : ''})`}
          size="small"
          color="warning"
          variant="soft"
          sx={{ fontWeight: 600 }}
        />
      );
    }
    if (row.BZ_required) {
      chips.push(
        <Chip
          key="BZ"
          label={`Busy (${row.BZ_required.count}${row.BZ_required.countMode === 'percent' ? '%' : ''})`}
          size="small"
          color="default"
          variant="soft"
          sx={{ fontWeight: 600 }}
        />
      );
    }
    if (row.O_required) {
      chips.push(
        <Chip
          key="O"
          label={`Other (${row.O_required.count}${row.O_required.countMode === 'percent' ? '%' : ''})`}
          size="small"
          color="default"
          variant="soft"
          sx={{ fontWeight: 600 }}
        />
      );
    }
    if (row.IVR_required) {
      chips.push(
        <Chip
          key="IVR"
          label={`IVR: ${row.IVR_required.ivrTitle || row.IVR_required.ivrId || 'Update'}`}
          size="small"
          color="info"
          variant="soft"
          sx={{ fontWeight: 600 }}
        />
      );
    }
    if (row.DTMF_required) {
      chips.push(
        <Chip
          key="DTMF"
          label={`DTMF (${row.DTMF_required.nodes?.length || 0} node)`}
          size="small"
          color="secondary"
          variant="soft"
          sx={{ fontWeight: 600 }}
        />
      );
    }
    if (row.CallDuration_required) {
      chips.push(
        <Chip
          key="CallDuration"
          label={`Duration (${row.CallDuration_required.min || 0}s–${row.CallDuration_required.max || 0}s)`}
          size="small"
          color="success"
          variant="soft"
          sx={{ fontWeight: 600 }}
        />
      );
    }
    if (row.DateTime_required) {
      chips.push(
        <Chip
          key="DateTime"
          label="Date &amp; Time"
          size="small"
          color="info"
          variant="soft"
          sx={{ fontWeight: 600 }}
        />
      );
    }

    if (chips.length === 0) {
      return (
        <Typography variant="caption" color="text.disabled">
          None
        </Typography>
      );
    }

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 300 }}>
        {chips}
      </Box>
    );
  };

  const isFiltering = search || actionFilter !== 'all' || statusFilter !== 'all';

  return (
    <Card sx={{ borderRadius: 2, border: (theme) => `1px solid ${theme.palette.divider}`, boxShadow: 'none' }}>
      {/* Search and Filters Bar */}
      <Box
        sx={{
          p: 2.5,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Stack direction="row" spacing={1.5} flexWrap="wrap" alignItems="center" sx={{ flex: 1 }}>
          <TextField
            size="small"
            placeholder="Search by Campaign ID"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            sx={{ width: { xs: '100%', sm: 280 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Action Type</InputLabel>
            <Select
              value={actionFilter}
              label="Action Type"
              onChange={(e) => {
                setActionFilter(e.target.value);
                setPage(0);
              }}
            >
              {ACTION_TYPE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(0);
              }}
            >
              {STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {isFiltering && (
            <Button
              size="small"
              variant="text"
              color="error"
              startIcon={<FilterListOffIcon />}
              onClick={handleResetFilters}
            >
              Reset
            </Button>
          )}
        </Stack>

        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchSettings} disabled={loading} size="small">
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* Error state */}
      {error && !loading && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={fetchSettings}>
              Retry
            </Button>
          }
          sx={{ m: 2 }}
        >
          {error}
        </Alert>
      )}

      {/* Table */}
      <TableContainer component={Paper} elevation={0}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 700, width: 80 }}>Seq ID</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Campaign ID(s)</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Selected User</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Configured Actions</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, width: 140 }}>Update Existing</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Requested By</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Created Date</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, width: 100 }}>Status</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              Array.from({ length: rowsPerPage > 5 ? 5 : rowsPerPage }).map((_, index) => (
                <TableRow key={index}>
                  <TableCell><Skeleton width={40} /></TableCell>
                  <TableCell><Skeleton width={80} /></TableCell>
                  <TableCell><Skeleton width={100} /></TableCell>
                  <TableCell><Skeleton width={180} /></TableCell>
                  <TableCell align="center"><Skeleton width={50} sx={{ mx: 'auto' }} /></TableCell>
                  <TableCell><Skeleton width={120} /></TableCell>
                  <TableCell><Skeleton width={110} /></TableCell>
                  <TableCell align="center"><Skeleton width={60} sx={{ mx: 'auto' }} /></TableCell>
                  <TableCell align="center"><Skeleton variant="circular" width={28} height={28} sx={{ mx: 'auto' }} /></TableCell>
                </TableRow>
              ))
            ) : filteredData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                  <Typography variant="subtitle1" color="text.secondary" sx={{ fontWeight: 600 }}>
                    No Admin Setting records found
                  </Typography>
                  <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>
                    {isFiltering ? 'Try adjusting your search filters.' : 'No settings have been submitted yet.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredData.map((row) => {
                const isPending = row.status === 0;
                const formattedDate = row.create_date
                  ? (typeof row.create_date === 'number'
                      ? (row.create_date < 1e11 ? moment.unix(row.create_date) : moment(row.create_date))
                      : moment(row.create_date)
                    ).format('DD MMM YYYY, hh:mm A')
                  : '—';

                return (
                  <TableRow
                    key={row._id || row.seq_id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleOpenDetails(row)}
                  >
                    {/* Seq ID */}
                    <TableCell sx={{ fontWeight: 700, fontFamily: 'monospace', color: 'primary.main' }}>
                      #{row.seq_id ?? '—'}
                    </TableCell>

                    {/* Campaign IDs */}
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(row.campaignIds || []).map((cid) => (
                          <Chip
                            key={cid}
                            label={cid}
                            size="small"
                            variant="outlined"
                            sx={{ fontFamily: 'monospace', fontWeight: 600, height: 22 }}
                            onClick={(e) => handleCopy(cid, e)}
                            icon={<ContentCopyIcon sx={{ fontSize: '12px !important' }} />}
                          />
                        ))}
                      </Box>
                    </TableCell>

                    {/* Selected User */}
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                        {row.selectedUserId || '—'}
                      </Typography>
                      {row.email && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {row.email}
                        </Typography>
                      )}
                    </TableCell>

                    {/* Configured Actions */}
                    <TableCell>{renderActionChips(row)}</TableCell>

                    {/* Update Existing Records */}
                    <TableCell align="center">
                      {row.updateExistingRecords ? (
                        <Chip label="Yes" size="small" color="primary" variant="soft" sx={{ fontWeight: 600 }} />
                      ) : (
                        <Chip label="No" size="small" variant="outlined" sx={{ color: 'text.secondary' }} />
                      )}
                    </TableCell>

                    {/* Requested By */}
                    <TableCell>
                      <Typography variant="body2">{row.requested_by || '—'}</Typography>
                    </TableCell>

                    {/* Created Date */}
                    <TableCell>
                      <Typography variant="body2">{formattedDate}</Typography>
                    </TableCell>

                    {/* Status */}
                    <TableCell align="center">
                      <Chip
                        label={isPending ? 'Pending' : 'Processed'}
                        size="small"
                        color={isPending ? 'warning' : 'success'}
                        variant="soft"
                        sx={{ fontWeight: 600 }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />

      {/* Details Modal */}
      <AdminSettingDetailsModal
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        setting={selectedSetting}
      />
    </Card>
  );
}
